import React, {
  FunctionComponent,
  useEffect,
  useState,
  useRef,
  useCallback
} from 'react';
import { useAppSelector } from 'hooks/redux';
import { fetchSearchResults } from 'features/search/searchSlice';
import { useDispatch } from 'react-redux';
import { useInView } from 'react-intersection-observer';
import { Recording } from 'features/recordings/recordingsSlice';
import { Loader } from 'components/Loader';
import { Event } from 'features/events/eventsSlice';
import { SearchBar } from './Components/SearchBar';
import { SearchFilters } from './Components/SearchFilters';
import { SearchEmpty } from './Components/SearchEmpty';
import SearchResultEvent from './Components/SearchResult/Event';
import SearchResultRecording from './Components/SearchResult/Recording';
import { SearchWrapper } from './Components/SearchWrapper';

export const Search: FunctionComponent = () => {
  const searchQuery = useAppSelector((state) => state.search.searchQuery);
  const searchType = useAppSelector((state) => state.search.searchType);
  const loading = useAppSelector((state) => state.search.isLoading);
  const pageCount = useAppSelector((state) => state.search.pageCount);
  const channelId = useAppSelector(
    (state) => state.channel.channelDetails?.channelId
  );
  const dispatch = useDispatch();
  const results = useAppSelector((state) => state.search.results);
  const [currentItems, setCurrentItems] = useState<Event[] | Recording[]>([]);
  const [itemOffset, setItemOffset] = useState(0);
  const currentPageRef = useRef(1);
  const { ref, inView } = useInView({
    threshold: 0
  });
  const itemsPerPage = 20;

  const getResults = useCallback(
    (pageNumber: number) => {
      if (!channelId || !searchQuery) return;
      dispatch(
        fetchSearchResults(pageNumber, undefined, searchQuery, channelId)
      );
    },
    [searchQuery, channelId, dispatch]
  );

  useEffect(
    function fetchResultsOnSearchQueryChange() {
      setCurrentItems([]);
      getResults(1);
    },
    [searchQuery, getResults]
  );

  useEffect(
    function fetchResultsOnTypeChange() {
      setCurrentItems([]);
      getResults(1);
    },
    [searchType, getResults]
  );

  useEffect(
    function setCurrentSearchResults() {
      const endOffset = itemOffset + itemsPerPage;
      console.log(`Loading results from ${itemOffset} to ${endOffset}`);
      setCurrentItems(results);
    },
    [itemOffset, itemsPerPage, results]
  );

  useEffect(
    function fetchRecordingsOnScroll() {
      if (inView && currentPageRef.current <= pageCount) {
        getResults(currentPageRef.current + 1);
        const newOffset = currentPageRef.current * itemsPerPage;
        setItemOffset(newOffset);
        currentPageRef.current += 1;
      }
    },
    [inView, getResults, itemsPerPage, pageCount]
  );

  return (
    <SearchWrapper>
      <SearchBar />
      <SearchFilters />
      {currentItems.length > 0 ? (
        <ul className="list--reset grid">
          {currentItems.map((result, index) => {
            const lastElement = index === currentItems.length - 1;
            if (searchType === 'events') {
              return (
                <SearchResultEvent
                  key={result.id}
                  result={result as Event}
                  lastElement={lastElement}
                  ref={ref}
                />
              );
            } else {
              return (
                <SearchResultRecording
                  key={result.id}
                  result={result as Recording}
                  lastElement={lastElement}
                  ref={ref}
                />
              );
            }
          })}
        </ul>
      ) : (
        !loading && <SearchEmpty />
      )}
      {loading && !inView && <Loader fillComponent={true} />}
      {loading && inView && (
        <div className="text--center mar-t mar-b">
          <Loader />
        </div>
      )}
    </SearchWrapper>
  );
};
